// Small shared UI pieces (panels, stat tiles, pills, banners, wizard).
import { useEffect, useId, useRef } from "react";
import { Icon } from "./Icon.jsx";

export function Panel({ title, desc, full, children, headerRight, headingLevel = 2 }) {
  const Heading = `h${headingLevel}`;
  return (
    <div className={"bi-panel" + (full ? " full" : "")}>
      {(title || headerRight) && (
        <div className="bi-panel-head">
          <div>
            {title && <Heading>{title}</Heading>}
            {desc && <div className="desc">{desc}</div>}
          </div>
          {headerRight}
        </div>
      )}
      {children}
    </div>
  );
}

export function StatTile({ k, v, d, tone }) {
  return (
    <div className={"bi-stat" + (tone ? ` ${tone}` : "")}>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
      {d && <div className="d">{d}</div>}
    </div>
  );
}

const STATUS_TONES = {
  pass: "ok",
  warn: "warn",
  fail: "bad",
  ok: "ok",
  draft: "neutral",
  ready_for_review: "warn",
  approved: "blue",
  deployed: "ok",
  completed: "neutral",
  cancelled: "neutral",
  active: "ok",
  proposed: "warn",
  retired: "neutral",
  "n/a": "neutral",
  current: "ok",
  stale: "warn",
  missing: "bad",
  unknown: "neutral",
  complete: "ok",
  blocked: "bad",
  awaiting_review: "warn",
  available: "blue",
  published: "ok",
  not_started: "neutral",
  unavailable: "neutral",
};

const STATUS_LABELS = {
  pass: "Automated checks passed",
  fail: "Blocked",
  ready_for_review: "Ready for review",
  deployed: "Published",
  "n/a": "Unavailable",
  current: "Current",
  stale: "Stale",
  missing: "Missing",
  unknown: "Freshness unavailable",
};

function statusLabel(status) {
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  const normalized = String(status).replace(/_/g, " ");
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function StatusPill({ status, label }) {
  const tone = STATUS_TONES[status] || "neutral";
  return <span className={`st-pill ${tone}`}>{label || statusLabel(status)}</span>;
}

export function LifecycleStrip({ items }) {
  return (
    <ol className="campaign-lifecycle" aria-label="Campaign lifecycle">
      {items.map((item) => (
        <li key={item.label} className={`campaign-lifecycle-item ${item.status || "not_started"}`}>
          <span className="campaign-lifecycle-label">{item.label}</span>
          <StatusPill status={item.status || "not_started"} label={item.statusLabel} />
          {item.detail && <small>{item.detail}</small>}
        </li>
      ))}
    </ol>
  );
}

function formatPlainDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(date);
}

export function EvidencePeriodHeader({
  period,
  periodLabel = "Evidence period",
  refreshedAt,
  freshness = "unknown",
  freshnessLabel,
  compact = false,
}) {
  const effectiveFreshness = period ? freshness : "missing";
  return (
    <section
      className={`evidence-period-context${compact ? " compact" : ""}`}
      aria-label="Evidence period and data freshness"
    >
      <div>
        <span className="evidence-period-label">{periodLabel}</span>
        <strong>{period || "Unavailable"}</strong>
      </div>
      <div>
        <span className="evidence-period-label">Refresh date</span>
        <strong>{formatPlainDate(refreshedAt) || "Unavailable"}</strong>
      </div>
      <StatusPill status={effectiveFreshness} label={freshnessLabel} />
    </section>
  );
}

export function MetricValue({ value, grain, suppressed = false, minimum, unavailableReason }) {
  if (suppressed) {
    return (
      <span className="metric-value suppressed" aria-label={`Suppressed ${grain || "count"}`}>
        <strong>{minimum ? `< ${minimum}` : "Suppressed"}</strong>
        {grain && <small>{grain}</small>}
      </span>
    );
  }
  if (value == null) {
    const reason = unavailableReason || "No measured value is available";
    return (
      <span
        className="metric-value unavailable"
        aria-label={`Unavailable${grain ? ` ${grain}` : ""}: ${reason}`}
      >
        <strong>Unavailable</strong>
        {grain && <small>{grain}</small>}
        <small>{reason}</small>
      </span>
    );
  }
  return (
    <span className="metric-value">
      <strong>{formatCount(value)}</strong>
      {grain && <small>{grain}</small>}
    </span>
  );
}

export function MissingEvidenceState({ reason, effect, action }) {
  return (
    <div className="missing-evidence-state" role="status">
      <strong>Unavailable</strong>
      <span>{reason}</span>
      {effect && <small>{effect}</small>}
      {action}
    </div>
  );
}

export function RestrictedBoundary({ authorized, returnHref, children }) {
  return (
    <section
      className={`restricted-boundary${authorized ? " authorized" : " denied"}`}
      aria-labelledby="restricted-boundary-title"
    >
      <div className="restricted-boundary-icon" aria-hidden="true">
        <Icon name="lock" size={22} strokeWidth={2} />
      </div>
      <div className="restricted-boundary-content">
        <span className="restricted-boundary-eyebrow">Restricted workspace</span>
        <h2 id="restricted-boundary-title">
          {authorized
            ? "Exact-person review is isolated from aggregate workflows"
            : "Restricted clearance required"}
        </h2>
        <p>
          {authorized
            ? "This audited workspace may display identifiers and claims-derived evidence. Its results, filters, and identifiers cannot be transferred into campaigns or aggregate Activate records."
            : "Secure Review is available only to authorized opportunity reviewers. Aggregate Activate screens remain available without restricted access."}
        </p>
        {children}
        <a className="restricted-boundary-return" href={returnHref}>
          <Icon name="chev-left" size={14} strokeWidth={2} /> Return to Activate overview
        </a>
      </div>
      <StatusPill
        status={authorized ? "approved" : "blocked"}
        label={authorized ? "Authorized session" : "Access unavailable"}
      />
    </section>
  );
}

export function GateBanner({ gate }) {
  if (!gate || gate.status === "warn") return null;
  const text = {
    pass: "Campaign data quality: all checks passed - eligible for campaign use",
    fail: `Campaign data quality: ${gate.failCount} check(s) failed - this data is blocked from campaign use`,
  }[gate.status];
  return (
    <div className={`gate-banner ${gate.status}`} role={gate.status === "fail" ? "alert" : "status"}>
      <Icon name={gate.status === "pass" ? "check" : "shield"} size={16} strokeWidth={2} />
      <span>{text}</span>
    </div>
  );
}

export function WizardSteps({ steps, current }) {
  return (
    <div className="wizard-steps" role="list" aria-label="Campaign creation progress">
      {steps.map((label, i) => (
        <div
          key={label}
          className={"wizard-step" + (i === current ? " active" : i < current ? " done" : "")}
          role="listitem"
          aria-current={i === current ? "step" : undefined}
        >
          <span className="n" aria-hidden="true">
            {i < current ? "ok" : i + 1}
          </span>{" "}
          {label}
        </div>
      ))}
    </div>
  );
}

// Plain-language summaries of the implemented checks documented in
// docs/SYSTEM_WORKFLOW_AND_DATA.md. Keep these aligned with backend governance.py.
const GOVERNANCE_CHECK_HELP = {
  quality_gate:
    "Passes when the data contains eligible people and actionable opportunities. Guardrail suppression above 20% shows a warning.",
  latest_run: "Passes when the campaign uses the latest available month for the selected program.",
  min_cohort:
    "Passes when the estimated audience meets the minimum privacy size. Smaller audiences are blocked.",
  guardrail_exclusion: "Shows how many covered people were excluded from the campaign by guardrails.",
  enrollment_alignment:
    "Goal 1 requires people who are not enrolled; Goal 2 requires people who are enrolled.",
  template_coverage:
    "Passes when every planned message uses an active template that covers the selected audience.",
  message_content: "Passes when every planned Workday app touch has the required message text.",
  sensitivity_channel: "Shows the audience sensitivity tier and the planned delivery channel for review.",
  cadence_caps: "Warns when this audience may overlap another active campaign for the same program.",
  safety_disclaimer: "Passes when every message that requires safety guidance includes it.",
};

const GOVERNANCE_CHECK_LABELS = {
  quality_gate: "Campaign data quality",
  latest_run: "Latest evidence period",
  min_cohort: "Minimum audience size",
  guardrail_exclusion: "Guardrail exclusion",
  enrollment_alignment: "Goal and enrollment alignment",
  template_coverage: "Template coverage",
  message_content: "Campaign message content",
  sensitivity_channel: "Sensitivity and channel",
  cadence_caps: "Campaign overlap",
  safety_disclaimer: "Safety disclaimer",
};

function governanceCheckLabel(check) {
  return GOVERNANCE_CHECK_LABELS[check.id] || check.label;
}

function GovernanceCheckTooltip({ check }) {
  const tooltipId = useId();
  const label = governanceCheckLabel(check);
  const help =
    check.help ||
    GOVERNANCE_CHECK_HELP[check.id] ||
    "Explains the policy condition this governance check evaluates before campaign approval or publication.";

  return (
    <span className="gov-tooltip">
      <button
        className="gov-tooltip-trigger"
        type="button"
        aria-label={`What ${label} checks`}
        aria-describedby={tooltipId}
      >
        <Icon name="info" size={13} strokeWidth={2} />
      </button>
      <span className="gov-tooltip-content" id={tooltipId} role="tooltip">
        <strong>{label}</strong>
        <span>{help}</span>
      </span>
    </span>
  );
}

export function GovernanceChecklist({ result }) {
  if (!result) return null;
  return (
    <div>
      {result.checks.map((check) => (
        <div key={check.id} className="gov-row">
          <span className={`gov-dot ${check.status}`}>{check.status === "pass" ? "ok" : "!"}</span>
          <span className="lbl">
            <span>{governanceCheckLabel(check)}</span>
            <GovernanceCheckTooltip check={check} />
          </span>
          <span className="det">{check.detail}</span>
        </div>
      ))}
    </div>
  );
}

export function Loading() {
  return (
    <div className="loading-card" role="status" aria-live="polite">
      Loading...
    </div>
  );
}

export function ErrorCard({ error }) {
  const errorRef = useRef(null);

  useEffect(() => {
    errorRef.current?.focus();
  }, []);

  return (
    <div ref={errorRef} className="error-card" role="alert" aria-atomic="true" tabIndex={-1}>
      Failed to load: {String(error?.message || error)}
    </div>
  );
}

export function formatPct(value, digits = 0) {
  if (value == null) return "Unavailable";
  return (value * 100).toFixed(digits) + "%";
}

export function formatCount(value) {
  if (value == null) return "Unavailable";
  return value.toLocaleString();
}
